import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, book, confirmCode, fetchFormToken, fetchSlots, isHold, locationLabel, requestSlot, resendCode, type Availability, type Booked, type HoldStarted, type Location, type PublicBrand, type PublicEventType } from '../api';
import { addMonths, formatDateTime, formatDay, formatMonth, formatTime, formatTimeThenDate, monthDays, monthKeyOf, todayKey, WEEKDAYS_SHORT, weekdayIndex } from '../format';

/**
 * Buchungsseite (Konzept 2) im klassischen Aufbau von Calendly und Cal.com: eine Karte, links der Anbieter mit
 * Terminart, Dauer, Ort und Zeitzone, rechts erst Monatskalender und Uhrzeiten, dann das Formular, dann die
 * Bestätigung. Die freien Zeiten kommen in einem Rutsch für den ganzen Horizont (die Slots-Route kennt keinen
 * Zeitraum) – der Kalender blättert nur durch das, was schon da ist.
 */
export function BookPage({ brand, eventType }: { brand: PublicBrand; eventType: PublicEventType }) {
  const [availability, setAvailability] = useState<Availability | null | undefined>(undefined);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [done, setDone] = useState<Booked | null>(null);
  // E-Mail-Code vor der Buchung: der Termin ist reserviert, der Kunde tippt den Code aus seiner Mail ein.
  const [hold, setHold] = useState<HoldStarted | null>(null);
  // Die Eingaben bleiben stehen, wenn der Kunde zum Formular zurückgeht („E-Mail-Adresse ändern“).
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);

  const load = () =>
    fetchSlots(brand.slug, eventType.slug)
      .then((a) => {
        setAvailability(a);
        setDay((d) => (d && a.days.some((x) => x.day === d) ? d : a.days[0]?.day ?? null));
      })
      .catch(() => setAvailability(null));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.slug, eventType.slug]);

  const days = availability?.days ?? [];
  const picking = !done && !slot && !!availability && !availability.unavailable && days.length > 0;

  // Reservierung abgelaufen oder Termin inzwischen weg: zurück zur Terminwahl mit frischen Zeiten.
  function startOver() {
    setHold(null);
    setSlot(null);
    void load();
  }

  let main: ReactNode;
  if (done && done.status === 'pending') {
    main = (
      <div className="confirm">
        <h2 className="confirm__title">Deine Anfrage ist eingegangen</h2>
        <p className="booked-when">{formatDateTime(done.startsAt)}</p>
        <p className="muted">
          {eventType.title} · {eventType.durationMinutes} Minuten · {locationLabel(eventType.location)}
        </p>
        <p>{brand.name} schaut sich deine Anfrage an und meldet sich per E-Mail. Die Zeit ist bis dahin für dich reserviert.</p>
        <p className="muted small">Du bekommst gleich eine E-Mail mit allen Angaben – darin steht auch, wie du die Anfrage zurückziehst oder verschiebst.</p>
        {!done.created && <p className="muted small">Diese Anfrage hattest du schon gestellt – sie gilt weiterhin.</p>}
      </div>
    );
  } else if (done) {
    main = (
      <div className="confirm">
        <h2 className="confirm__title">Dein Termin steht</h2>
        <p className="booked-when">{formatDateTime(done.startsAt)}</p>
        <p className="muted">
          {eventType.title} · {eventType.durationMinutes} Minuten · {locationLabel(eventType.location)}
        </p>
        <p>{eventType.location === 'meet' ? 'Den Link zum Gespräch bekommst du per E-Mail.' : 'Wir rufen dich zur vereinbarten Zeit an.'}</p>
        <p className="muted small">Die Bestätigung mit allen Angaben ist unterwegs an deine E-Mail-Adresse – darin steht auch, wie du den Termin verschiebst oder absagst.</p>
        {!done.created && <p className="muted small">Diesen Termin hattest du schon gebucht – er gilt weiterhin.</p>}
      </div>
    );
  } else if (hold && slot) {
    main = (
      <CodeStep
        brand={brand}
        eventType={eventType}
        hold={hold}
        onDone={setDone}
        onChangeEmail={() => setHold(null)}
        onStartOver={startOver}
      />
    );
  } else if (availability === undefined) main = <p className="muted scheduler__status">Freie Termine werden geladen …</p>;
  else if (availability === null) main = <div className="error">Die Termine lassen sich gerade nicht laden. Bitte versuch es gleich noch einmal.</div>;
  else if (availability.unavailable) main = <RequestForm brand={brand} eventType={eventType} />;
  else if (days.length === 0) main = <div className="notice">Zurzeit ist leider nichts frei. Schau in ein paar Tagen noch einmal vorbei.</div>;
  else if (!slot) main = <SlotPicker days={days} day={day} onDay={setDay} onPick={setSlot} />;
  else
    main = (
      <BookingForm
        brand={brand}
        eventType={eventType}
        slot={slot}
        initial={formValues}
        replaceHoldId={hold?.holdId}
        onBack={() => setSlot(null)}
        onTaken={() => {
          setSlot(null);
          void load();
        }}
        onDone={setDone}
        onHold={(h, values) => {
          setFormValues(values);
          setHold(h);
        }}
      />
    );

  return (
    <SchedulerCard wide={picking} info={<ProviderInfo brand={brand} eventType={eventType} chosen={done?.startsAt ?? slot} />}>
      {main}
    </SchedulerCard>
  );
}

/** Die Karte: links der Anbieter, rechts der jeweilige Schritt. `wide` gibt dem Kalender Platz für drei Spalten. */
export function SchedulerCard({ info, wide, children }: { info: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <section className={`scheduler${wide ? ' scheduler--wide' : ''}`}>
      <aside className="scheduler__info">{info}</aside>
      <div className="scheduler__main">{children}</div>
    </section>
  );
}

interface InfoBrand {
  name: string;
  logoUrl: string | null;
}
interface InfoEventType {
  title: string;
  description?: string | null;
  durationMinutes: number;
  location: Location;
}

/** Linke Spalte: Logo oder Anfangsbuchstabe, Name, Terminart, Dauer, Ort, Zeitzone – und der gewählte Termin in Grün. */
export function ProviderInfo({ brand, eventType, chosen, heading = 'h1', children }: { brand: InfoBrand; eventType: InfoEventType; chosen?: string | null; heading?: 'h1' | 'h2'; children?: ReactNode }) {
  const Title = heading;
  return (
    <>
      <div className="scheduler__head">
        {brand.logoUrl ? <img className="scheduler__logo" src={brand.logoUrl} alt="" /> : <span className="scheduler__avatar">{brand.name.trim().charAt(0).toUpperCase()}</span>}
        <div>
          <p className="scheduler__brand">{brand.name}</p>
          <Title className="scheduler__title">{eventType.title}</Title>
        </div>
      </div>
      {eventType.description && <p className="scheduler__desc">{eventType.description}</p>}
      <ul className="scheduler__meta">
        <li>
          <ClockIcon />
          <span>{eventType.durationMinutes} Minuten</span>
        </li>
        <li>
          {eventType.location === 'meet' ? <VideoIcon /> : <PhoneIcon />}
          <span>{locationLabel(eventType.location)}</span>
        </li>
        <li>
          <GlobeIcon />
          <span>Deutsche Zeit (Europe/Berlin)</span>
        </li>
        {chosen && (
          <li className="scheduler__chosen">
            <CalendarIcon />
            <span>{formatTimeThenDate(chosen)}</span>
          </li>
        )}
      </ul>
      {children}
    </>
  );
}

/**
 * Monatskalender in der Mitte, Uhrzeiten rechts – auch fürs Umbuchen (manage.tsx).
 * Beim Öffnen ist der erste Tag mit freien Zeiten gewählt; zurück geht es nur bis zum aktuellen Monat, vor nur bis
 * zum Monat des letzten freien Tags. Ein Monat ohne freie Zeiten sagt das rechts, statt leer zu bleiben.
 */
export function SlotPicker({ days, day, onDay, onPick, disabled }: { days: Availability['days']; day?: string | null; onDay?: (day: string) => void; onPick: (slot: string) => void; disabled?: boolean }) {
  const [ownDay, setOwnDay] = useState<string | null>(null);
  const current = (day !== undefined ? day : ownDay) ?? days[0]?.day ?? null;
  const select = (d: string) => (onDay ? onDay(d) : setOwnDay(d));
  const selectedDay = days.find((d) => d.day === current) ?? days[0] ?? null;

  const today = todayKey();
  const firstMonth = monthKeyOf(today);
  const lastDay = days[days.length - 1]?.day ?? today;
  const lastMonth = monthKeyOf(lastDay) > firstMonth ? monthKeyOf(lastDay) : firstMonth;
  const [month, setMonth] = useState(() => monthKeyOf(selectedDay?.day ?? today));

  const free = new Map(days.map((d) => [d.day, d.slots]));
  const cells = monthDays(month);
  const monthHasFree = cells.some((c) => free.has(c));
  const lead = weekdayIndex(cells[0]!);

  function go(step: 1 | -1) {
    const next = addMonths(month, step);
    if (next < firstMonth || next > lastMonth) return;
    setMonth(next);
    // Wechselt der Monat und liegt der gewählte Tag nicht darin, wird der erste freie Tag des neuen Monats gewählt.
    if (selectedDay && monthKeyOf(selectedDay.day) !== next) {
      const firstFree = monthDays(next).find((c) => free.has(c));
      if (firstFree) select(firstFree);
    }
  }

  const [weekday, rest] = selectedDay ? splitDay(formatDay(selectedDay.day)) : ['', ''];

  return (
    <div className="picker">
      <div className="calendar">
        <div className="calendar__head">
          <p className="calendar__month">
            <strong>{formatMonth(month).replace(/ \d{4}$/, '')}</strong> {month.slice(0, 4)}
          </p>
          <div className="calendar__nav">
            <button type="button" aria-label="Vorheriger Monat" disabled={month <= firstMonth} onClick={() => go(-1)}>
              <ChevronIcon direction="left" />
            </button>
            <button type="button" aria-label="Nächster Monat" disabled={month >= lastMonth} onClick={() => go(1)}>
              <ChevronIcon direction="right" />
            </button>
          </div>
        </div>
        <div className="calendar__grid" role="grid">
          {WEEKDAYS_SHORT.map((w) => (
            <span key={w} className="calendar__weekday">
              {w}
            </span>
          ))}
          {Array.from({ length: lead }, (_, i) => (
            <span key={`lead-${i}`} aria-hidden="true" />
          ))}
          {cells.map((c) => {
            const slots = free.get(c);
            const isSelected = selectedDay?.day === c;
            const cls = ['calendar__day', slots ? 'is-free' : '', isSelected ? 'is-selected' : '', c === today ? 'is-today' : '', c < today ? 'is-past' : ''].filter(Boolean).join(' ');
            return (
              <button key={c} type="button" className={cls} disabled={!slots} aria-pressed={isSelected} aria-label={formatDay(c)} onClick={() => select(c)}>
                {Number(c.slice(8))}
              </button>
            );
          })}
        </div>
      </div>
      <div className="times">
        {!monthHasFree ? (
          <p className="muted times__empty">{month < lastMonth ? 'In diesem Monat ist nichts frei – schau im nächsten.' : 'In diesem Monat ist nichts frei.'}</p>
        ) : selectedDay ? (
          <>
            <p className="times__head">
              <strong>{weekday}</strong>
              {rest}
            </p>
            <div className="times__list">
              {selectedDay.slots.map((s) => (
                <button key={s} type="button" className="times__slot" disabled={disabled} onClick={() => onPick(s)}>
                  {formatTime(s)}
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="muted times__empty">Wähl einen Tag.</p>
        )}
      </div>
    </div>
  );
}

/** „Freitag, 1. Juli" → ['Freitag', ', 1. Juli'] für die Überschrift der Uhrzeiten. */
function splitDay(text: string): [string, string] {
  const i = text.indexOf(',');
  return i === -1 ? [text, ''] : [text.slice(0, i), text.slice(i)];
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}
const EMPTY_FORM: FormValues = { name: '', email: '', phone: '', company: '', message: '' };

function BookingForm({ brand, eventType, slot, initial, replaceHoldId, onBack, onTaken, onDone, onHold }: { brand: PublicBrand; eventType: PublicEventType; slot: string; initial: FormValues; replaceHoldId?: string | undefined; onBack: () => void; onTaken: () => void; onDone: (b: Booked) => void; onHold: (hold: HoldStarted, values: FormValues) => void }) {
  const [formToken, setFormToken] = useState('');
  const [name, setName] = useState(initial.name);
  const [email, setEmail] = useState(initial.email);
  const [phone, setPhone] = useState(initial.phone);
  const [company, setCompany] = useState(initial.company);
  const [message, setMessage] = useState(initial.message);
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFormToken().then((t) => setFormToken(t.token)).catch(() => undefined);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await book(brand.slug, eventType.slug, { startsAt: slot, name, email, phone, company, message, formToken, website, ...(replaceHoldId ? { holdId: replaceHoldId } : {}) });
      if (isHold(outcome)) onHold(outcome, { name, email, phone, company, message });
      else onDone(outcome);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      if (code === 'slot_taken') {
        setError('Diese Uhrzeit ist gerade vergeben worden. Bitte such dir eine andere aus.');
        setTimeout(onTaken, 2500);
      } else if (code === 'invalid_email') setError('Bitte prüfe deine E-Mail-Adresse.');
      else if (code === 'unknown_domain') setError('Diese E-Mail-Adresse scheint nicht zu existieren. Bitte prüfe die Schreibweise.');
      else if (code === 'rate_limited') setError('Das waren gerade zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else if (code === 'rejected') setError('Das hat nicht geklappt. Bitte lade die Seite neu und versuch es noch einmal.');
      else if (code === 'google_unavailable') {
        setError('Gerade können wir den Kalender nicht prüfen. Bitte versuch es gleich noch einmal.');
        setTimeout(onTaken, 2500);
      } else if (code === 'mail_failed') setError('Die E-Mail mit dem Code ließ sich gerade nicht verschicken. Bitte versuch es gleich noch einmal.');
      else setError('Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="booking-form" onSubmit={submit}>
      <div className="field">
        <label htmlFor="name">Dein Name</label>
        <input id="name" type="text" required maxLength={200} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="email">E-Mail-Adresse</label>
        <input id="email" type="email" required maxLength={254} autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <span className="muted small">Dahin geht die Bestätigung{eventType.location === 'meet' ? ' mit dem Link zum Gespräch' : ''}.</span>
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="phone">Telefon (freiwillig)</label>
          <input id="phone" type="tel" maxLength={60} autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="company">Praxis oder Firma (freiwillig)</label>
          <input id="company" type="text" maxLength={200} autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="message">Worum soll es gehen? (freiwillig)</label>
        <textarea id="message" maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      {/* Honigtopf: für Menschen unsichtbar, für Bots ein normales Feld. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      {error && <div className="error">{error}</div>}
      <div className="booking-form__actions">
        <button type="submit" className="button" disabled={busy || !formToken}>
          {busy ? 'Einen Moment …' : 'Termin buchen'}
        </button>
        <button type="button" className="button button--secondary" onClick={onBack}>
          Zurück
        </button>
      </div>
      {(brand.privacyUrl || brand.imprintUrl) && (
        <p className="faint small">
          Deine Angaben werden nur für diesen Termin genutzt.{' '}
          {brand.privacyUrl && (
            <a href={brand.privacyUrl} target="_blank" rel="noreferrer">
              Datenschutz
            </a>
          )}
        </p>
      )}
    </form>
  );
}

/**
 * E-Mail-Code vor der Buchung (Spam-Schutz Stufe 1): Der Termin ist zehn Minuten reserviert, der Kunde tippt den
 * 6-stelligen Code aus seiner Mail ein – große Ziffern, automatisch weiter, bei sechs Ziffern geht es von selbst los.
 * „Code erneut senden“ höchstens zweimal, „E-Mail-Adresse ändern“ zurück zum Formular (die Reservierung bleibt, bis
 * das Formular neu abgeschickt wird). Läuft die Zeit ab, bietet die Seite „Zeit neu wählen“ an.
 */
const CODE_LENGTH = 6;

function CodeStep({ brand, eventType, hold, onDone, onChangeEmail, onStartOver }: { brand: PublicBrand; eventType: PublicEventType; hold: HoldStarted; onDone: (b: Booked) => void; onChangeEmail: () => void; onStartOver: () => void }) {
  const [digits, setDigits] = useState<string[]>(() => Array.from({ length: CODE_LENGTH }, () => ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [resendsLeft, setResendsLeft] = useState(2);
  const [resent, setResent] = useState(false);
  const [remaining, setRemaining] = useState(() => secondsLeft(hold.expiresAt));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const submitted = useRef('');

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const left = secondsLeft(hold.expiresAt);
      setRemaining(left);
      if (left <= 0) setExpired(true);
    }, 1000);
    return () => clearInterval(timer);
  }, [hold.expiresAt]);

  const code = digits.join('');

  async function submit(value = code) {
    if (value.length !== CODE_LENGTH || busy || submitted.current === value) return;
    submitted.current = value;
    setBusy(true);
    setError(null);
    try {
      onDone(await confirmCode(brand.slug, eventType.slug, hold.holdId, value));
    } catch (e) {
      const apiError = e instanceof ApiError ? e : null;
      const reason = apiError?.code ?? '';
      if (reason === 'code_invalid') {
        const left = typeof apiError?.details['attemptsLeft'] === 'number' ? (apiError.details['attemptsLeft'] as number) : null;
        setError(left === 1 ? 'Der Code stimmt nicht. Du hast noch einen Versuch.' : left ? `Der Code stimmt nicht. Du hast noch ${left} Versuche.` : 'Der Code stimmt nicht.');
        setDigits(Array.from({ length: CODE_LENGTH }, () => ''));
        submitted.current = '';
        setTimeout(() => inputs.current[0]?.focus(), 0);
      } else if (reason === 'hold_expired' || reason === 'hold_not_found') setExpired(true);
      else if (reason === 'slot_taken') {
        setError('Diese Uhrzeit ist inzwischen vergeben. Bitte such dir eine andere aus.');
        setTimeout(onStartOver, 2500);
      } else if (reason === 'google_unavailable') {
        setError('Gerade können wir den Kalender nicht prüfen. Bitte versuch es gleich noch einmal.');
        submitted.current = '';
      } else if (reason === 'rate_limited') setError('Das waren gerade zu viele Versuche. Bitte warte einen Moment.');
      else {
        setError('Das hat nicht geklappt. Bitte versuch es noch einmal.');
        submitted.current = '';
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    setResent(false);
    try {
      const r = await resendCode(brand.slug, eventType.slug, hold.holdId);
      setResendsLeft(r.resendsLeft);
      setResent(true);
      setDigits(Array.from({ length: CODE_LENGTH }, () => ''));
      submitted.current = '';
      inputs.current[0]?.focus();
    } catch (e) {
      const reason = e instanceof ApiError ? e.code : '';
      if (reason === 'resend_limit') {
        setResendsLeft(0);
        setError('Mehr als zweimal geht leider nicht. Prüf bitte auch deinen Spam-Ordner.');
      } else if (reason === 'hold_expired' || reason === 'hold_not_found') setExpired(true);
      else if (reason === 'rate_limited') setError('Das waren gerade zu viele Versuche. Bitte warte einen Moment.');
      else setError('Die E-Mail ließ sich gerade nicht verschicken. Bitte versuch es gleich noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  function setDigit(index: number, raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      setDigits((d) => d.map((v, i) => (i === index ? '' : v)));
      return;
    }
    // Mehrere Ziffern auf einmal (Einfügen, Autovervollständigung): auf die Felder verteilen.
    const next = [...digits];
    let cursor = index;
    for (const ch of clean) {
      if (cursor >= CODE_LENGTH) break;
      next[cursor] = ch;
      cursor += 1;
    }
    setDigits(next);
    inputs.current[Math.min(cursor, CODE_LENGTH - 1)]?.focus();
    const full = next.join('');
    if (full.length === CODE_LENGTH) void submit(full);
  }

  function onKey(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      setDigits((d) => d.map((v, i) => (i === index - 1 ? '' : v)));
      inputs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
    else if (event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData('text');
    if (/\d/.test(text)) {
      event.preventDefault();
      setDigit(0, text);
    }
  }

  if (expired) {
    return (
      <div className="confirm code-step">
        <h2 className="confirm__title">Die Reservierung ist abgelaufen</h2>
        <p>Der Termin war zehn Minuten für dich reserviert. Die Zeit ist um – such dir einfach noch einmal eine Uhrzeit aus.</p>
        <div className="booking-form__actions">
          <button type="button" className="button" onClick={onStartOver}>
            Zeit neu wählen
          </button>
        </div>
      </div>
    );
  }

  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, '0');

  return (
    <div className="code-step">
      <h2 className="confirm__title">Fast fertig – bitte den Code eingeben</h2>
      <p>
        Wir haben dir einen Code an <strong>{hold.email}</strong> geschickt. Gib ihn hier ein, dann ist der Termin gebucht.
      </p>
      <div className="code-step__inputs" role="group" aria-label="6-stelliger Code aus der E-Mail">
        {digits.map((value, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            className="code-step__digit"
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            aria-label={`Ziffer ${i + 1}`}
            value={value}
            disabled={busy}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKey(i, e)}
            onPaste={onPaste}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>
      <p className={`code-step__timer${remaining <= 60 ? ' is-urgent' : ''}`} aria-live="polite">
        Der Termin ist reserviert – noch {minutes}:{seconds}
      </p>
      {resent && !error && <p className="success-inline">Ein neuer Code ist unterwegs. Der alte gilt nicht mehr.</p>}
      {error && <div className="error">{error}</div>}
      <div className="booking-form__actions">
        <button type="button" className="button" disabled={busy || code.length !== CODE_LENGTH} onClick={() => void submit()}>
          {busy ? 'Einen Moment …' : 'Bestätigen'}
        </button>
        <button type="button" className="button button--secondary" disabled={busy || resendsLeft <= 0} onClick={() => void resend()}>
          {resendsLeft > 0 ? 'Code erneut senden' : 'Code erneut senden (aufgebraucht)'}
        </button>
        <button type="button" className="button button--secondary" disabled={busy} onClick={onChangeEmail}>
          E-Mail-Adresse ändern
        </button>
      </div>
      <p className="faint small">Keine Mail bekommen? Schau bitte auch im Spam-Ordner nach. Der Code ist zehn Minuten gültig.</p>
    </div>
  );
}

function secondsLeft(expiresAt: string): number {
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

/**
 * Terminwunsch (Konzept 5.4): Google antwortet nicht, also gibt es keine Zeiten – aber der Kunde geht nicht leer aus.
 * Name und E-Mail, derselbe Botschutz wie beim Buchen; die Mail mit dem Link zur Terminwahl kommt von selbst.
 */
function RequestForm({ brand, eventType }: { brand: PublicBrand; eventType: PublicEventType }) {
  const [formToken, setFormToken] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetchFormToken().then((t) => setFormToken(t.token)).catch(() => undefined);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestSlot(brand.slug, eventType.slug, { name, email, formToken, website });
      setDone(true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      if (code === 'invalid_email') setError('Bitte prüfe deine E-Mail-Adresse.');
      else if (code === 'unknown_domain') setError('Diese E-Mail-Adresse scheint nicht zu existieren. Bitte prüfe die Schreibweise.');
      else if (code === 'rate_limited') setError('Das waren gerade zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else if (code === 'not_applicable') setError('Bitte lade die Seite neu – die Terminauswahl ist wieder da.');
      else setError('Das hat nicht geklappt. Bitte lade die Seite neu und versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="confirm">
        <h2 className="confirm__title">Danke, wir melden uns</h2>
        <p>Sobald die Terminauswahl wieder da ist, bekommst du eine E-Mail an {email.trim()} mit dem Link – meist dauert das nicht lange.</p>
      </div>
    );
  }

  return (
    <form className="booking-form" onSubmit={submit}>
      <div>
        <p className="label">Gerade keine Zeiten</p>
        <p>Gerade können wir keine Zeiten anzeigen. Hinterlass uns deine E-Mail – wir melden uns, sobald die Terminauswahl wieder da ist.</p>
      </div>
      <div className="field">
        <label htmlFor="req-name">Dein Name</label>
        <input id="req-name" type="text" required maxLength={200} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="req-email">E-Mail-Adresse</label>
        <input id="req-email" type="email" required maxLength={254} autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      {/* Honigtopf: für Menschen unsichtbar, für Bots ein normales Feld. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor="req-website">Website</label>
        <input id="req-website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      {error && <div className="error">{error}</div>}
      <div className="booking-form__actions">
        <button type="submit" className="button" disabled={busy || !formToken}>
          {busy ? 'Einen Moment …' : 'Bescheid geben, sobald es wieder geht'}
        </button>
      </div>
      {brand.privacyUrl && (
        <p className="faint small">
          Deine Angaben werden nur für diese Nachricht genutzt.{' '}
          <a href={brand.privacyUrl} target="_blank" rel="noreferrer">
            Datenschutz
          </a>
        </p>
      )}
    </form>
  );
}

/* Symbole als Inline-SVG – keine fremden Icon-Pakete (CSP: nur 'self'). */

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function ClockIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  );
}

function VideoIcon() {
  return (
    <Icon>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3z" />
    </Icon>
  );
}

function PhoneIcon() {
  return (
    <Icon>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </Icon>
  );
}

function GlobeIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </Icon>
  );
}

export function CalendarIcon() {
  return (
    <Icon>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </Icon>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return <Icon>{direction === 'left' ? <path d="m15 6-6 6 6 6" /> : <path d="m9 6 6 6-6 6" />}</Icon>;
}
